import { FC, useMemo, useState } from 'react'

import { useAtomValue, useSetAtom } from 'jotai'
import { Button, PageTitle, Flex } from '@sequencehq/core-components'
import { usePermissions } from 'lib/rbac'
import {
  editingVersionIdAtom,
  formattedAutomationNameAtom,
  hasAnyChangesAtom,
  hasPresentationChangedAtom,
  showValidationResultsAtom,
  validationPopoverKeyAtom,
  validationResultsAtom,
  activeVersionIdAtom,
  isEditingDisabledAtom
} from 'Automations/automationEditor/atoms'
import { hasBlockingValidationResults } from 'Automations/nodeDefinitions'
import { useAutomationId } from 'Automations/automationEditor/hooks/useAutomationId/useAutomationId'
import { useEditingTriggerType } from 'Automations/automationEditor/hooks/useEditingTriggerType/useEditingTriggerType'
import { useDuplicateActiveTriggerCheck } from 'Automations/automationsManagement/components/WorkflowCard/useDuplicateActiveTriggerCheck'
import { UndoRedo } from './UndoRedo'
import { Title } from './Title'
import { KebabMenu } from './KebabMenu'
import { ModalActivateVersion, ModalSaveVersion } from './modals'
import { WorkflowDetails } from './WorkflowDetails'

export const Header: FC = () => {
  const automationName = useAtomValue(formattedAutomationNameAtom)

  return (
    <Flex padding="0 8px" height="48px" alignItems="center" position="relative">
      <PageTitle title={[automationName, 'Workflows']} />
      <UndoRedo />
      <Title />

      <Flex gap="8px" marginLeft="auto">
        <WorkflowDetails />
        <ButtonSaveVersion />
        <ButtonActivateVersion />
        <KebabMenu />
      </Flex>
    </Flex>
  )
}

/**
 * NOTE: the endpoints these buttons use require different permissions, e.g create vs. update.
 * This doesn't currently matter given that roles have the same granular permissions, so
 * we're just using the 'update' permission to keep the logic simple.
 */

const ButtonSaveVersion: FC = () => {
  const { can } = usePermissions()

  const [showModal, setShowModal] = useState<boolean>(false)

  const hasChanges = useAtomValue(hasAnyChangesAtom)
  const isEditingDisabled = useAtomValue(isEditingDisabledAtom)

  const hasPermissions = can('org.watchtower:update')
  if (!hasPermissions) return null

  return (
    <>
      <Button
        data-testid="automations.editor.save"
        variant="secondary"
        onClick={() => setShowModal(true)}
        disabled={isEditingDisabled || !hasChanges}
      >
        Save
      </Button>
      {showModal && <ModalSaveVersion onClose={() => setShowModal(false)} />}
    </>
  )
}

const ButtonActivateVersion: FC = () => {
  const { can } = usePermissions()

  const validationResults = useAtomValue(validationResultsAtom)
  const setShowValidationResults = useSetAtom(showValidationResultsAtom)
  const setValidationPopoverKey = useSetAtom(validationPopoverKeyAtom)
  const hasPresentationChanged = useAtomValue(hasPresentationChangedAtom)
  const editingVersionId = useAtomValue(editingVersionIdAtom)
  const activeVersionId = useAtomValue(activeVersionIdAtom)
  const isEditingDisabled = useAtomValue(isEditingDisabledAtom)
  const { automationId } = useAutomationId()
  const triggerType = useEditingTriggerType()

  const [showModal, setShowModal] = useState<boolean>(false)

  const { isLoading: isConflictLoading, conflict } =
    useDuplicateActiveTriggerCheck({ automationId, triggerType })

  const canActivate = useMemo(() => {
    if (isEditingDisabled) return false

    // if the currently editing version is not the active version
    if (editingVersionId !== activeVersionId) return true

    // if there are unsaved presentation changes
    if (hasPresentationChanged) return true

    return false
  }, [
    hasPresentationChanged,
    editingVersionId,
    activeVersionId,
    isEditingDisabled
  ])

  // `validationResultsAtom` is derived synchronously from the live presentation
  // and the resolved registry, so the gate always reflects the current graph —
  // no async re-validation or stale-window race.
  const onPublishClick = () => {
    if (hasBlockingValidationResults(validationResults)) {
      setShowValidationResults(true)
      setValidationPopoverKey(prev => prev + 1)
      return
    }

    // The generatability check runs when the version is activated (see
    // useActivateAutomationVersion), so it guards every publish entry point —
    // not just this button.
    setShowModal(true)
  }

  const hasPermissions = can('org.watchtower:update')
  if (!hasPermissions) return null

  return (
    <>
      <Button
        data-testid="automations.editor.publish"
        variant="primary"
        onClick={onPublishClick}
        disabled={!canActivate || isConflictLoading}
      >
        Publish
      </Button>
      {showModal && (
        <ModalActivateVersion
          onClose={() => setShowModal(false)}
          conflict={conflict}
          triggerType={triggerType}
        />
      )}
    </>
  )
}
